import { useHistory } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import AppRouter from 'common/AppRouter';
import { isTeamLicenseSelector } from 'state/selectors/license';
import { getRootTeamSelector } from 'state/selectors/teams';
import { individualContributorsSelector } from 'state/selectors/contributors';
import * as TrackingService from 'common/services/tracking.service';
import { TRACKING } from 'common/constants/constants';
import { createCreateTeamModalOptions } from 'modals/modal-options-factory';
import {
  toggleModal,
  toggleSnackBar,
  createTeam,
  updateTeam,
  deleteTeam,
  getAccountByToken,
  updateTeamContributors,
  getTeams,
  deleteGroup,
  getAccounts
} from 'state/callers';
import { selectTeamId } from 'state/actions';
import {
  getOnEditTeam,
  onCreateTeam
} from '../../../../people/views/teams/teams.service';
import useToast from 'components/hooks/useToast';
import { selectedTeamSelector } from 'state/selectors/ui-state';
import useDeleteTeamModal from 'components/hooks/useDeleteTeamModal';
import { getAccountsNamesFormatted } from './useTeams.service';
import useSyncedTeamsWizard from './SyncGitTeamsWizard/useSyncedTeamsWizard';

const useTeams = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const isTeamLicense = useSelector(isTeamLicenseSelector);

  const contributors = useSelector(individualContributorsSelector);
  const selectedTeam = useSelector(selectedTeamSelector) || {};
  const rootTeamId = useSelector(getRootTeamSelector);

  const confirmModal = useDeleteTeamModal();
  const toast = useToast();
  const { toggleSyncTeamsModal } = useSyncedTeamsWizard();

  const onDeleteTeam = async (team, accounts) => {
    const teamName = team?.name ?? '';
    const teamId = team?.id;

    const confirmOptions = {
      title: 'Delete Team',
      confirmText: 'Yes, Delete',
      width: '600px',
      accountsNames: getAccountsNamesFormatted(accounts, teamId),
      teamName,
      type: ' team'
    };

    const isConfirmed = await confirmModal(confirmOptions);
    if (!isConfirmed) return;

    try {
      await deleteTeam(team.id)(dispatch);

      dispatch(getTeams());
      dispatch(getAccounts());

      if (selectedTeam.id === team.id) {
        dispatch(selectTeamId());
      }

      toast({
        message: `Team ${teamName} was deleted successfully`,
        variant: 'success'
      });
      TrackingService.logEvent(TRACKING.EVENTS.CLICK, {
        page: TRACKING.PAGES.SETTINGS,
        page_tab: TRACKING.PAGE_TABS.TEAMS,
        click_type: TRACKING.CLICK.DELETE_TEAM
      });
    } catch (e) {
      toast({
        message: `oops.. something went wrong`,
        variant: 'error'
      });
    }
  };

  const onDeleteGroup = async (team, accounts) => {
    const teamName = team?.name ?? '';
    const teamId = team?.id;

    const confirmOptions = {
      title: 'Delete Group',
      confirmText: 'Yes, Delete',
      width: '600px',
      accountsNames: getAccountsNamesFormatted(accounts, teamId),
      teamName,
      type: ' group',
      extraText: (
        <>
          <br />
          <br />
          If you proceed, all teams within this group will be moved under{' '}
          <b>All Teams</b>.
        </>
      )
    };

    const isConfirmed = await confirmModal(confirmOptions);
    if (!isConfirmed) return;

    try {
      await deleteGroup(team.id)(dispatch);

      dispatch(getTeams());
      dispatch(getAccounts());

      TrackingService.logEvent(TRACKING.EVENTS.CLICK, {
        page: TRACKING.PAGES.SETTINGS,
        page_tab: TRACKING.PAGE_TABS.TEAMS,
        click_type: TRACKING.CLICK.DELETE_GROUP
      });

      if (rootTeamId === team.id) {
        await getAccountByToken()(dispatch);
      }

      if (selectedTeam.id === team.id) {
        dispatch(selectTeamId());
      }

      toast({
        message: `Group ${teamName} was deleted successfully`,
        variant: 'success'
      });
    } catch (e) {
      toast({
        message: `oops.. something went wrong`,
        variant: 'error'
      });
    }
  };
  const toggleTeamFormModal = ({ team } = {}) => {
    const isEditMode = !!team?.id;

    if (!isEditMode) {
      TrackingService.logEvent(TRACKING.EVENTS.CLICK, {
        page: TRACKING.PAGES.SETTINGS,
        page_tab: TRACKING.PAGE_TABS.TEAMS,
        click_type: TRACKING.CLICK.OPEN_ADD_TEAM
      });
    }

    dispatch(
      toggleModal(
        createCreateTeamModalOptions({
          title: isEditMode ? 'Edit Team' : 'Add Team',
          ...(isEditMode ? { team: { ...team }, isEdit: true } : {}),
          contributors,
          onSubmit: isEditMode
            ? getOnEditTeam({
                updateTeam: team => dispatch(updateTeam(team)),
                selectedTeam: team,
                updateTeamContributors: (...args) =>
                  dispatch(updateTeamContributors(...args)),
                getTeams: () => dispatch(getTeams()),
                toggleModal: params => dispatch(toggleModal(params)),
                toggleSnackBar: params => dispatch(toggleSnackBar(params))
              })
            : onCreateTeam({
                createTeam: team => dispatch(createTeam(team)),
                toggleModal: params => dispatch(toggleModal(params)),
                toggleSnackBar: params => dispatch(toggleSnackBar(params)),
                getTeams: () => dispatch(getTeams())
              }),
          onCancel: () => dispatch(toggleModal())
        })
      )
    );
  };

  const goBack = () => {
    history.goBack();
  };

  const goToTeamsConfig = ({ team, isSelectTeamId = false } = {}) => {
    const isEditMode = !!team?.id;

    history.push({
      pathname: isEditMode
        ? `${AppRouter.TEAMS_SETTINGS.path}/${team?.id}`
        : AppRouter.TEAMS_SETTINGS_ADD.path,
      search: 'isOrgSettings=true',
      state: {
        isSelectTeamId
      }
    });
  };

  const goToGroupsConfig = team => {
    const isEditMode = !!team?.id;

    history.push({
      pathname: isEditMode
        ? `${AppRouter.TEAMS_SETTINGS.path}/${team?.id}`
        : AppRouter.GROUPS_SETTINGS_ADD.path,
      search: 'isOrgSettings=true'
    });
  };

  return {
    goBack,
    onConfigureTeam: !isTeamLicense ? goToTeamsConfig : toggleTeamFormModal,
    onDeleteTeam,
    onDeleteGroup,
    toggleSyncTeamsModal,
    onConfigureGroup: goToGroupsConfig
  };
};

export default useTeams;
